import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Target, TrendingUp, TrendingDown, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

const GoalsStatus = ({ goals, currentMetrics, timeRange }) => {
    // Provide fallback goals if none are provided
    const fallbackGoals = {
        revenue: { total: 5000 },
        users: { total: 1000 },
        ai: { adoption: { autofill: 15 } }
    };

    const safeGoals = goals || fallbackGoals;
    const safeCurrentMetrics = currentMetrics || {};

    if (!safeGoals) {
        return (
            <Card className="bg-card text-card-foreground shadow-sm">
                <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        Goals Status
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-sm text-muted-foreground">
                        Loading goal data...
                    </div>
                </CardContent>
            </Card>
        );
    }

    const getGoalStatus = (current, goal, metricType) => {
        // Handle undefined, null, or invalid values
        const safeCurrent = current || 0;
        const safeGoal = goal || 0;

        if (!safeGoal || safeGoal <= 0) return { status: 'no-goal', progress: 0, isOnTrack: false };

        const progress = (safeCurrent / safeGoal) * 100;
        const isOnTrack = progress >= 80; // 80% of goal is considered on track

        if (progress >= 100) return { status: 'exceeded', progress: 100, isOnTrack: true };
        if (isOnTrack) return { status: 'on-track', progress, isOnTrack: true };
        if (progress >= 50) return { status: 'behind', progress, isOnTrack: false };
        return { status: 'at-risk', progress, isOnTrack: false };
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'exceeded':
            case 'on-track':
                return <CheckCircle className="h-4 w-4 text-emerald-500" />;
            case 'behind':
                return <AlertCircle className="h-4 w-4 text-yellow-500" />;
            case 'at-risk':
                return <AlertCircle className="h-4 w-4 text-red-500" />;
            default:
                return <Target className="h-4 w-4 text-muted-foreground" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'exceeded':
            case 'on-track':
                return 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/20';
            case 'behind':
                return 'text-yellow-500 bg-yellow-50 dark:bg-yellow-950/20';
            case 'at-risk':
                return 'text-red-500 bg-red-50 dark:bg-red-950/20';
            default:
                return 'text-muted-foreground bg-muted';
        }
    };

    const formatValue = (value, type = 'number') => {
        // Handle undefined, null, or NaN values
        if (value === undefined || value === null || isNaN(value)) {
            return type === 'currency' ? '$0' : type === 'percentage' ? '0.0%' : '0';
        }

        if (type === 'currency') {
            return `$${value.toLocaleString()}`;
        }
        if (type === 'percentage') {
            return `${value.toFixed(1)}%`;
        }
        return value.toLocaleString();
    };

    const goalMetrics = [
        {
            name: 'Revenue',
            current: safeCurrentMetrics?.totalRevenue || 0,
            goal: safeGoals?.revenue?.total || 5000,
            type: 'currency',
            icon: TrendingUp
        },
        {
            name: 'Users',
            current: safeCurrentMetrics?.totalUsers || 0,
            goal: safeGoals?.users?.total || 1000,
            type: 'number',
            icon: TrendingUp
        },
        {
            name: 'AI Usage',
            current: safeCurrentMetrics?.aiUsage || 0,
            goal: safeGoals?.ai?.adoption?.autofill || 15,
            type: 'number',
            icon: TrendingUp
        }
    ];

    return (
        <Card className="bg-card text-card-foreground shadow-sm">
            <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Goals Status
                    <Badge variant="outline" className="ml-auto">
                        {timeRange}d
                    </Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {goalMetrics.map((metric) => {
                    const status = getGoalStatus(metric.current, metric.goal, metric.name);
                    const Icon = metric.icon;

                    return (
                        <div key={metric.name} className="space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Icon className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm font-medium">{metric.name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">
                                        {formatValue(metric.current, metric.type)} / {formatValue(metric.goal, metric.type)}
                                    </span>
                                    {getStatusIcon(status.status)}
                                </div>
                            </div>

                            <div className="space-y-1">
                                <Progress
                                    value={status.progress}
                                    className={cn(
                                        "h-2",
                                        status.status === 'exceeded' || status.status === 'on-track'
                                            ? "bg-emerald-500/20"
                                            : status.status === 'behind'
                                                ? "bg-yellow-500/20"
                                                : "bg-red-500/20"
                                    )}
                                />
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">
                                        {(status.progress || 0).toFixed(1)}% complete
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className={cn("text-xs", getStatusColor(status.status))}
                                    >
                                        {status.status.replace('-', ' ')}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    );
                })}

                <div className="pt-2 border-t border-border">
                    <div className="text-xs text-muted-foreground">
                        Goals are automatically adjusted based on your time range selection.
                        Customize goals in Settings → Goals & Tracking.
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default GoalsStatus; 